import { apiUrl, reportStorageUrl } from '@http/constants';

// A signed CDN download URL rejected with 403 once its token expires.
// Regenerating it requires a fresh fetch of the owning page's data, so the
// simplest fix is a full reload — this event lets a single, app-wide
// listener (ReactQueryServerErrorNotifications) prompt the user for that,
// regardless of which call site triggered the request.
export const SIGNED_URL_EXPIRED_EVENT = 'eye-able:signed-url-expired';

export type FetchWrapperParams =
  | Record<
      string,
      // a `headers` entry is pulled out as request headers; the rest become
      // query params
      string | number | boolean | null | undefined | Record<string, string>
    >
  | URLSearchParams
  // Callers pass null explicitly to skip params and reach later arguments.
  | null
  | undefined;

export type StreamRequestArgs = {
  url: string;
  body?: BodyInit | object | null;
  onChunkReceived?: (chunk: string) => void;
};

type NamedBlob = Blob & { fileName?: string };

// Shape the API uses for JSON error bodies; both keys are optional.
type JsonErrorBody = Record<string, unknown> & {
  message?: unknown;
  error?: unknown;
};

function addParamsToUrl(url: string, params: FetchWrapperParams): string {
  const queryString = new URLSearchParams(
    params as Record<string, string>,
  ).toString();
  return url.includes('?') ? `${url}&${queryString}` : `${url}?${queryString}`;
}

export function getRequestHeaders(
  url: string,
  contentType: string | null = 'application/json',
): Record<string, string> {
  const headers: Record<string, string> = {};
  if (contentType) headers['Content-Type'] = contentType;

  if (localStorage.getItem('demoMode') === 'true' && url.includes(apiUrl))
    headers['X-Demo-Mode'] = 'true';
  else if (!import.meta.env.PROD && url.includes(apiUrl))
    headers['X-Dev-Token'] = localStorage.getItem('X-Dev-Token') ?? '';

  // Act inside a guest organisation for this request, if one is selected.
  // Stored by selectedGuestOrgAtom (atomWithStorage JSON-encodes, so null -> 'null').
  const guestOrganisation = localStorage.getItem('X-Guest-Organisation');
  if (guestOrganisation && guestOrganisation !== 'null' && url.includes(apiUrl))
    headers['X-Guest-Organisation'] = guestOrganisation;

  return headers;
}

function getCredentials(url: string): RequestCredentials {
  return import.meta.env.PROD && url.includes(apiUrl) ? 'include' : 'omit';
}

function getBody(
  body: BodyInit | object | null | undefined,
  contentType: string | null,
): BodyInit | undefined {
  if (!body) return undefined;
  if (contentType === 'application/json') return JSON.stringify(body);
  return body as BodyInit;
}

type RequestOptions = {
  body?: BodyInit | object | null;
  params?: FetchWrapperParams;
  contentType?: string | null;
  // Merged over the derived headers; only `delete` passes these today.
  headers?: Record<string, string>;
};

/**
 * Shared request core: derives credentials and headers from the url, encodes
 * the body per `contentType`, and appends query params. Returns the raw
 * Response so `head` can read the status without going through
 * `handleResponse`.
 */
function request(
  method: string,
  url: string,
  { body, params, contentType = null, headers }: RequestOptions = {},
): Promise<Response> {
  const requestOptions: RequestInit = {
    method,
    credentials: getCredentials(url),
    headers: { ...getRequestHeaders(url, contentType), ...headers },
    body: getBody(body, contentType),
  };
  if (params) url = addParamsToUrl(url, params);
  return fetch(url, requestOptions);
}

function getDownloadFileName(response: Response): string | null {
  const contentDisposition = response.headers.get('Content-Disposition');
  const encodedFileNameMatch = contentDisposition?.match(
    /filename\*\s*=\s*UTF-8''([^;]+)/i,
  );

  if (encodedFileNameMatch?.[1]) {
    return decodeURIComponent(encodedFileNameMatch[1]);
  }

  const fileNameMatch = contentDisposition?.match(/filename\s*=\s*"([^"]+)"/i);
  return fileNameMatch?.[1] ?? null;
}

async function streamRequest({
  url,
  body,
  onChunkReceived,
}: StreamRequestArgs): Promise<string> {
  const response = await request('POST', url, {
    body,
    contentType: 'application/json',
  });

  if (!response.ok) {
    const status = response.status;
    const responseBody = await response.text();
    if (response.headers.get('Content-Type')?.includes('json')) {
      const error = JSON.parse(responseBody) as JsonErrorBody;
      const message = error.message || error.error || error;
      return Promise.reject({ ok: false, status, message });
    }

    const message =
      response.statusText + (responseBody ? `: ${responseBody}` : '');
    return Promise.reject({ ok: false, status, message });
  }

  if (!response.body) {
    return Promise.reject({
      ok: false,
      status: 0,
      message: 'The response ended unexpectedly.',
      reason: 'missing_body',
      partial: '',
    });
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');

  // Wire-protocol sentinels matching apps/api/src/ai/openai/openAIUtils.ts
  // Keep both copies in sync.
  const STREAM_ERROR_SENTINEL = '\n\n[[EYE_ABLE_STREAM_ERROR]]';
  const STREAM_OK_SENTINEL = '\n\n[[EYE_ABLE_STREAM_OK]]';

  let dataReceived = '';
  let streamError: { reason: string; message: string } | null = null;
  let streamCompletedCleanly = false;

  // Reading the stream
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      if (streamError) {
        return Promise.reject({
          ok: false,
          status: 0,
          message: streamError.message,
          reason: streamError.reason,
          partial: dataReceived,
        });
      }
      if (!streamCompletedCleanly && dataReceived.length > 0) {
        return Promise.reject({
          ok: false,
          status: 0,
          message: 'The response ended unexpectedly.',
          reason: 'missing_ok_sentinel',
          partial: dataReceived,
        });
      }
      break;
    }
    let chunk = decoder.decode(value);

    const okIdx = chunk.indexOf(STREAM_OK_SENTINEL);
    if (okIdx !== -1) {
      streamCompletedCleanly = true;
      chunk = chunk.slice(0, okIdx);
    }

    const sentinelIdx = chunk.indexOf(STREAM_ERROR_SENTINEL);
    if (sentinelIdx !== -1) {
      const beforeSentinel = chunk.slice(0, sentinelIdx);
      const afterSentinel = chunk.slice(
        sentinelIdx + STREAM_ERROR_SENTINEL.length,
      );
      const newlineIdx = afterSentinel.indexOf('\n');
      const payloadJson =
        newlineIdx === -1 ? afterSentinel : afterSentinel.slice(0, newlineIdx);
      let parsed: { reason?: string; detail?: { message?: string } };
      try {
        parsed = JSON.parse(payloadJson) as typeof parsed;
      } catch {
        parsed = { reason: 'unknown' };
      }
      streamError = {
        reason: parsed.reason || 'stream_error',
        message:
          parsed.detail?.message ||
          parsed.reason ||
          'The response ended unexpectedly.',
      };
      chunk = beforeSentinel;
    }

    if (chunk) {
      dataReceived += chunk;
      onChunkReceived?.(chunk);
    }
  }

  return dataReceived;
}

function get<T = unknown>(
  url: string,
  params?: FetchWrapperParams,
): Promise<T> {
  return request('GET', url, { params }).then(handleResponse) as Promise<T>;
}

function post<T = unknown>(
  url: string,
  body?: BodyInit | object | null,
  params?: FetchWrapperParams,
  contentType: string | null = 'application/json',
): Promise<T> {
  return request('POST', url, { body, params, contentType }).then(
    handleResponse,
  ) as Promise<T>;
}

function put<T = unknown>(
  url: string,
  body?: BodyInit | object | null,
  params?: FetchWrapperParams,
  contentType: string | null = 'application/json',
): Promise<T> {
  return request('PUT', url, { body, params, contentType }).then(
    handleResponse,
  ) as Promise<T>;
}

function deleteRequest<T = unknown>(
  url: string,
  params?: FetchWrapperParams,
): Promise<T> {
  // A `headers` entry becomes request headers; everything else is query params.
  const { headers, ...queryParams } = (params ?? {}) as Record<string, unknown>;

  return request('DELETE', url, {
    params: Object.keys(queryParams).length
      ? (queryParams as FetchWrapperParams)
      : undefined,
    headers: headers as Record<string, string> | undefined,
  }).then(handleResponse) as Promise<T>;
}

// HEAD request - returns just the status without body
function head(
  url: string,
  params?: FetchWrapperParams,
): Promise<{ ok: boolean; status: number }> {
  return request('HEAD', url, { params }).then((response) => ({
    ok: response.ok,
    status: response.status,
  }));
}

// A signed CDN URL carries `token`/`expires` params (see signReportStorageUrl
// on the API side) — require `token` too so an unrelated 403 from an
// unsigned reportStorageUrl path isn't misreported as an expired link.
function isExpiredSignedUrlResponse(response: Response): boolean {
  if (response.status !== 403 || !response.url?.startsWith(reportStorageUrl)) {
    return false;
  }
  return new URL(response.url).searchParams.has('token');
}

// reject promise if response is not ok and return server error message
async function handleResponse(response: Response): Promise<unknown> {
  if (isExpiredSignedUrlResponse(response)) {
    window.dispatchEvent(new Event(SIGNED_URL_EXPIRED_EVENT));
  }

  const contentType = response.headers.get('Content-Type');
  const isAttachment =
    response.headers
      .get('Content-Disposition')
      ?.toLowerCase()
      .includes('attachment') ?? false;

  // Treat any attachment (or known binary office/pdf types) as a downloadable
  // blob. `officedocument` covers Word, Excel and PowerPoint OOXML types.
  if (
    response.ok &&
    (isAttachment ||
      contentType?.includes('pdf') ||
      contentType?.includes('text/csv') ||
      contentType?.includes('officedocument'))
  ) {
    const blob: NamedBlob = await response.blob();
    const fileName = getDownloadFileName(response);

    if (fileName) {
      blob.fileName = fileName;
    }

    return blob;
  }

  const text = await response.text();

  if (!response.ok) {
    const errorResponse = response as Response & { message?: unknown };
    let message: unknown = response.statusText;
    if (text && response.headers.get('Content-Type')?.includes('json')) {
      try {
        const data = JSON.parse(text) as JsonErrorBody;
        message = data.message || data.error || data || message;
        errorResponse.json = () => Promise.resolve({ ...data, message });
      } catch {
        message = 'Invalid JSON response';
        errorResponse.json = () => Promise.resolve(message);
      }
    }

    errorResponse.message = message;
    errorResponse.text = () => Promise.resolve(message as string);

    return Promise.reject(errorResponse);
  }

  let data: unknown = text;
  if (text && response.headers.get('Content-Type')?.includes('json')) {
    try {
      data = JSON.parse(text);
    } catch {
      return Promise.reject({
        ok: false,
        status: 500,
        statusText: 'Invalid JSON response',
        message: 'Invalid JSON response',
      });
    }
  }

  return data;
}

const fetchWrapper = {
  get,
  post,
  put,
  delete: deleteRequest,
  streamRequest,
  head,
};

export default fetchWrapper;
